import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiPost } from '../lib/api.js';
import { useAuth } from '../store/auth.js';
import Logo from '../components/Logo.jsx';
import AIOrb from '../components/AIOrb.jsx';
import { Phone, MessageSquare, ShieldCheck } from 'lucide-react';

export default function Login() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [a2pPasscode, setA2pPasscode] = useState('');
  const [loading, setLoading] = useState(false);
  // 2FA second-step state. When mfaToken is set, render the code prompt
  // instead of the email/password form.
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const { setSession } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const isDemo = import.meta.env.VITE_DEMO_MODE === 'true';

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const url = mode === 'login' ? '/auth/login' : '/auth/register';
      const body = mode === 'login'
        ? { email, password }
        : { name, email, password, a2pPasscode: a2pPasscode.trim() || undefined };
      const r = await apiPost(url, body);
      // If the server says 2FA is required, switch to the code prompt instead
      // of completing login.
      if (r && r.mfaRequired && r.mfaToken) {
        setMfaToken(r.mfaToken);
        toast('Enter your authenticator code', { icon: '🔐' });
        return;
      }
      if (!r || !r.token) throw new Error('No token returned');
      setSession(r.token, r.agent);
      toast.success(mode === 'login' ? 'Welcome back' : 'Account created');
      const dest = loc.state?.from?.pathname || '/';
      // small delay so zustand state propagates before nav (StrictMode double-render)
      setTimeout(() => nav(dest, { replace: true }), 50);
    } catch (err) {
      console.error('Login error:', err);
      const msg = err.response?.data?.error || err.message || 'Login failed';
      if (isDemo) {
        // In demo mode, never block the user — drop them in with a synthetic session
        setSession('demo-token', { _id: 'demo', name: 'Easton Padden', email: email || 'easton@civix.local', role: 'superadmin', timezone: 'America/Denver' });
        toast.success('Welcome to the CIVIX demo');
        setTimeout(() => nav('/', { replace: true }), 50);
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  async function submitMfa(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await apiPost('/auth/2fa/verify', { token: mfaToken, code: mfaCode.trim() });
      if (!r || !r.token) throw new Error('No token returned');
      setSession(r.token, r.agent);
      toast.success('Welcome back');
      const dest = loc.state?.from?.pathname || '/';
      setTimeout(() => nav(dest, { replace: true }), 50);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'MFA verification failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  function enterDemo() {
    setSession('demo-token', { _id: 'demo', name: 'Easton Padden', email: 'easton@civix.local', role: 'superadmin', timezone: 'America/Denver' });
    toast.success('Welcome to the CIVIX demo');
    setTimeout(() => nav('/', { replace: true }), 50);
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-4 bg-civix-bg relative overflow-hidden">
      {/* Background animated gradients */}
      <div className="absolute inset-0 bg-civix-radial pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-civix-purple/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-civix-cyan/20 rounded-full blur-3xl" />

      <div className="panel w-full max-w-5xl grid md:grid-cols-2 overflow-hidden relative">
        {/* Left — brand */}
        <div className="hidden md:flex flex-col justify-between p-10 bg-linear-to-br from-civix-purpleDeep/30 to-transparent border-r border-civix-border relative overflow-hidden">
          <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-civix-purple/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-28 -right-16 w-72 h-72 rounded-full bg-civix-cyan/15 blur-3xl pointer-events-none" />
          <div className="relative"><Logo size="lg" /></div>
          <div className="relative flex flex-col items-center gap-6">
            <AIOrb size={220} />
            <div className="text-center">
              <div className="font-display text-2xl font-semibold gradient-text mb-1">Hey CIVIX</div>
              <div className="text-sm text-white/60 max-w-xs">
                Your AI sales co-pilot — power dialing, compliant texting, and instant call summaries in one workspace.
              </div>
            </div>
          </div>
          <div className="relative space-y-2.5">
            {[
              { icon: Phone, text: 'Power Dialer with recordings & AI call summaries' },
              { icon: MessageSquare, text: 'Two-way SMS, iMessage & drip campaigns' },
              { icon: ShieldCheck, text: 'Built-in A2P compliance, recycling & retargeting' },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2.5 text-xs text-white/55">
                <span className="w-6 h-6 rounded-md flex items-center justify-center bg-white/4 border border-civix-border shrink-0">
                  <f.icon className="w-3.5 h-3.5 text-civix-purpleSoft" />
                </span>
                <span>{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right — form */}
        <div className="p-8 md:p-10">
          <div className="md:hidden mb-8"><Logo size="md" /></div>
          <h1 className="text-3xl font-display font-bold mb-1">
            {mode === 'login' ? 'Welcome back' : 'Create account'}
          </h1>
          <p className="text-white/50 mb-8">
            {mode === 'login' ? 'Sign in to your CIVIX workspace.' : 'Join the autopilot revolution.'}
          </p>

          {mfaToken ? (
            <form onSubmit={submitMfa} className="space-y-4">
              <div className="text-sm text-white/70 mb-2">
                Enter the 6-digit code from your authenticator app (or a backup code).
              </div>
              <div>
                <label className="label">Code</label>
                <input
                  className="input mt-1 font-mono tracking-widest"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="123456"
                  autoFocus
                  required
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full mt-2 disabled:opacity-60">
                {loading ? 'Verifying…' : 'Verify'}
              </button>
              <button
                type="button"
                className="btn-ghost w-full text-xs"
                onClick={() => { setMfaToken(''); setMfaCode(''); }}
              >
                Cancel
              </button>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="label">Full Name</label>
                <input className="input mt-1" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
            )}
            {mode === 'register' && (
              <div>
                <label className="label flex items-center justify-between">
                  <span>A2P Campaign Passcode <span className="text-white/40 font-normal">(optional)</span></span>
                </label>
                <input
                  className="input mt-1 font-mono"
                  value={a2pPasscode}
                  onChange={(e) => setA2pPasscode(e.target.value)}
                  placeholder="a2p_xxxxxxxxxxxxxxxx"
                />
                <div className="text-[11px] text-white/40 mt-1">
                  Got a passcode from your manager? Paste it here to instantly gain SMS + calling access. You can also add it later in Settings.
                </div>
              </div>
            )}
            <div>
              <label className="label">Email</label>
              <input type="email" className="input mt-1" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <label className="label flex items-center justify-between">
                <span>Password</span>
                {mode === 'login' && (
                  <Link to="/forgot-password" className="text-xs text-civix-purpleSoft hover:text-civix-cyan font-normal">
                    Forgot password?
                  </Link>
                )}
              </label>
              <input type="password" className="input mt-1" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full mt-2 disabled:opacity-60">
              {loading ? 'Working…' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          </form>
          )}

          <div className="mt-6 text-center text-sm text-white/50">
            {mode === 'login' ? (
              <>New to CIVIX? <button className="text-civix-purpleSoft hover:text-civix-cyan" onClick={() => setMode('register')}>Create an account</button></>
            ) : (
              <>Have an account? <button className="text-civix-purpleSoft hover:text-civix-cyan" onClick={() => setMode('login')}>Sign in</button></>
            )}
          </div>

          {isDemo && (
            <button onClick={enterDemo} className="btn-ghost w-full mt-3 border border-civix-border">
              Enter Demo (skip login)
            </button>
          )}

          <div className="mt-8 pt-4 border-t border-civix-border/50 text-center text-xs text-white/40 flex items-center justify-center gap-3">
            <Link to="/privacy" className="hover:text-white/70">Privacy</Link>
            <span className="text-white/20">·</span>
            <Link to="/terms" className="hover:text-white/70">Terms</Link>
            <span className="text-white/20">·</span>
            <Link to="/sms-policy" className="hover:text-white/70">SMS Policy</Link>
          </div>

        </div>
      </div>
    </main>
  );
}
